import { config } from "../../shared/config/environment";
import { fetchWithRefresh, type RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { HttpStatusError } from "../../shared/api/pollBackoff";
import { hostToChannel, type Channel } from "../../shared/host";
import type { Activity, Decision } from "../types";

/**
 * The Office host this add-in instance is running in, or null when it can't be
 * told (Office.js not loaded, or a host we don't map). Read at submit time
 * rather than threaded through the panel's props: it's a fact about the whole
 * add-in instance, and by the time a user can click Approve, Office is ready.
 */
function currentHostChannel(): Channel | null {
  if (typeof Office === "undefined") return null;
  return hostToChannel(Office.context?.host ?? null);
}

// The background-HITL endpoints live on the Python FastAPI service
// (apps/backend/ai/API/routes/v1/agent_runtime.py), so this module routes
// through `aiApiBaseUrl`, mirroring src/chat/services/api.ts.

/**
 * Headers for backend API calls: JSON content type (when posting) and the
 * MSAL bearer token when present. Mirrors `buildChatHeaders` in
 * src/chat/services/api.ts.
 */
function buildHeaders(apiToken: string | null, contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  if (apiToken) {
    headers["Authorization"] = `Bearer ${apiToken}`;
  }
  return headers;
}

/** Statuses the Actions list shows: pending cards plus ones answered in the
 *  last ~3 weeks (the record's own TTL), so a decided card survives a reload. */
export const LISTED_STATUSES = "awaiting_input,decided,resuming,completed";

/** Fetch the HITL decisions the signed-in user is allowed to see — pending and
 *  already-answered (see LISTED_STATUSES). */
export async function listActivities(
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<Activity[]> {
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/agent-runtime/activities?status=${LISTED_STATUSES}`,
    {
      headers: buildHeaders(apiToken),
      // The Office WebView can replay a stale GET; `no-store` keeps polling
      // honest so a just-resolved approval doesn't reappear from cache. We use
      // the fetch `cache` option (not a `Cache-Control` request header) on
      // purpose — a custom header would add `cache-control` to the CORS
      // preflight, which the APIM gateway's allow-list rejects.
      cache: "no-store",
    },
    refresh,
  );
  if (!response.ok) {
    throw new HttpStatusError(`Failed to load approvals: ${response.status}`, response.status);
  }
  return response.json() as Promise<Activity[]>;
}

/**
 * Submit a human decision and enqueue the run's resume.
 *
 * The backend feeds `decision` verbatim to the parked `interrupt()`. The HITL
 * tool treats `ok: false` as a *frontend error* (it returns an error to the
 * agent), so a real human decision — approve OR reject — is always `ok: true`
 * with the chosen option in `data.decision`; the skill branches on that value.
 *
 * `source` names the Office host the decision was made in; the backend stamps
 * it onto the decision as `approval_source` so the skill trace shows where the
 * approval came from. Omitted when the host is unknown — the backend then
 * records it as `unknown`.
 */
export async function resumeActivity(
  apiToken: string | null,
  activity: Pick<Activity, "run_id" | "interrupt_id">,
  decision: Decision,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const data: Record<string, unknown> = { decision: decision.decision };
  if (decision.feedback) {
    data.feedback = decision.feedback;
  }
  if (decision.inputs && Object.keys(decision.inputs).length > 0) {
    data.inputs = decision.inputs;
  }
  const body: Record<string, unknown> = {
    run_id: activity.run_id,
    interrupt_id: activity.interrupt_id,
    decision: { ok: true, data },
  };
  const source = currentHostChannel();
  if (source) {
    body.source = source;
  }
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/agent-runtime/resume`,
    {
      method: "POST",
      headers: buildHeaders(apiToken, "application/json"),
      body: JSON.stringify(body),
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(await submitFailureMessage(response));
  }
}

/** The server's own reason when it gives one (a 400 names the field and its
 *  limit), else the bare status — never swallow what the API said. */
async function submitFailureMessage(response: Response): Promise<string> {
  const fallback = `Failed to submit decision: ${response.status}`;
  try {
    const body: unknown = await response.json();
    const detail = (body as { detail?: unknown } | null)?.detail;
    return typeof detail === "string" && detail.trim() ? `Could not submit: ${detail}` : fallback;
  } catch {
    return fallback;
  }
}
